import { Button } from '@cube3/cubicle/src/core/atoms/Button';
import { ModalNoticeUI } from '@cube3/ui/src/Modal/ModalNoticeUI';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useModalActions } from '../../Modals/modalActions';
import {
  clearSubmitErrors,
  getFormValues,
  InjectedFormProps,
  reduxForm
} from 'redux-form';
import { compose } from '../../../../../utils/component-helpers';
import { useCurrentUser } from '@cube3/state/src/redux/components/Administration/withCurrentUserAccount';
import { useMutateResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useMutateResource';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '@cube3/state/src/redux/components/Hooks/useTypedSelector';
import { actionCreators } from '@cube3/state/src/redux/ducks/session';
import { addFormId } from '@cube3/state/src/redux/middleware/redux-form-middleware';
import { statuses } from '@cube3/state/src/redux/ducks/request-status';
import { hasResolved } from '@cube3/state/src/redux/ducks/request-status/utils/hasResolved';
import { Message } from '@cube3/cubicle/src/core/atoms/InputField/Message';
import { RequestError } from '@cube3/common/model/types';
import { serverErrorCodes } from '@cube3/forms/src/utils/errorCodes';
import { GenericReduxForm } from '@cube3/forms/src/forms/GenericReduxForm';
import { Field } from '@cube3/forms/src/types/GenericFormTypes';

interface Props extends InjectedFormProps {
  modalContext: {
    name: string;
    email: string;
  };
}
const mutateConfig = { cacheInvalidator: (r) => [r] };

const ChangeAccountDetailsModalBase = (props: Props) => {
  const {
    // modal received props
    modalContext,
    // form state
    form,
    error,
    submitting,
    submitSucceeded
  } = props;

  const { email, name } = modalContext;
  const password = useTypedSelector((state) => getFormValues(form)(state))?.[
    'password'
  ];

  const { previousModal, openModal } = useModalActions();

  const [userId] = useCurrentUser();

  const [mutateResource, mutateResourceStatus] =
    useMutateResource__ALPHA(mutateConfig);

  const dispatch = useDispatch();
  const handleSave = useCallback(() => {
    if (name) {
      mutateResource({
        type: 'user',
        id: userId,
        full_name: name,
        password
      });
    }
    if (email) {
      dispatch(
        addFormId(form, { formState: undefined, useRequestStatus: true })(
          actionCreators.changeEmail({
            email,
            password,
            userId
          })
        )
      );
    }
  }, [mutateResource, name, email, password]);

  const fields = useMemo(() => {
    return [
      {
        type: 'password',
        name: 'password',
        placeholder: 'Enter password to confirm identity...',
        onEnter: handleSave
      } as Field<string, 'password'>
    ];
  }, [handleSave]);

  useEffect(() => {
    if (submitSucceeded) {
      openModal('accountsettings_confirmEmail', email);
    } else if (mutateResourceStatus === statuses.SUCCESS && !email) {
      previousModal();
    }
  }, [submitSucceeded, mutateResourceStatus]);

  const loading =
    submitting || (mutateResourceStatus && !hasResolved(mutateResourceStatus));

  let errorMsg = undefined;
  const err = error as unknown; // because redux thinks its a string (but its an array of objects) we have to cast it first
  const errors: RequestError[] = err as RequestError[];

  // when the form errors on submit...
  if (errors) {
    if (errors[0].status?.includes('403')) {
      errorMsg =
        'You are not allowed to change your email adress because your user role does not allow this';
    } else {
      if (errors[0].code === serverErrorCodes.HTTP_UNAUTHORIZED) {
        errorMsg = 'Current password is incorrect. Please check your password.';
      }
    }
  }

  return (
    <ModalNoticeUI
      title={'Enter password'}
      onCloseEvent={previousModal}
      loading={loading}
      footerLeftComponent={
        <Button label="Cancel" buttonStyle="outline" onClick={previousModal} />
      }
      footerRightComponent={
        <Button
          label="Save"
          buttonStyle="solid"
          background="primary"
          onClick={handleSave}
          disabled={loading || !!error || !password}
        />
      }
    >
      <form>
        <GenericReduxForm fields={fields} />
      </form>
      {error && <Message messageColor="critical" label={errorMsg} />}
    </ModalNoticeUI>
  );
};

export const ChangeAccountDetailsModal = compose(ChangeAccountDetailsModalBase)(
  reduxForm({
    form: 'change_account_name_email',
    destroyOnUnmount: true,
    onChange: (values, dispatch, props) => {
      // clear submit errors when changing input after submit failed
      if (values?.['password'] && props.submitFailed) {
        dispatch(clearSubmitErrors(props.form));
      }
    }
  })
);
